/**
===================================================
Module: frontend.src.components.SessionDetails.tsx
Description: Document details modal component with carousel and PDF viewer
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Session } from "../types";
import { apiService } from "../services/api";
import NotificationSystem, { Notification } from "./NotificationSystem";
import "../styles/SessionDetails.css";

interface SessionDetailsProps {
  session: Session;
  onClose: () => void;
  onUpdate?: (
    sessionId: string,
    updates: { name: string; description: string },
  ) => Promise<void>;
}

const SessionDetails: React.FC<SessionDetailsProps> = ({
  session,
  onClose,
  onUpdate,
}) => {
  const { t } = useTranslation('sessions');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(session.name);
  const [description, setDescription] = useState(session.description || "");
  const [isUpdating, setIsUpdating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Notification management functions
  const addNotification = (notification: Omit<Notification, 'id'>) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { ...notification, id }]);
    return id;
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Load PDF when component mounts
  useEffect(() => {
    const loadPdf = async () => {
      if (session.status === "completed") {
        setIsLoadingPdf(true);
        setPdfError(null);
        try {
          const url = await apiService.getSessionInputPdfUrl(session.id);
          setPdfUrl(url);
        } catch (error) {
          addNotification({
            type: 'error',
            message: t('details.loadPdfFailed', { ns: 'pdfEditor' })
          });
          setPdfError(t('details.loadPdfFailed', { ns: 'pdfEditor' }));
        } finally {
          setIsLoadingPdf(false);
        }
      }
    };

    loadPdf();

    // Cleanup blob URL when component unmounts
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [session.id, session.status]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };


  const handleSave = async () => {
    if (!onUpdate) return;

    try {
      setIsUpdating(true);
      await apiService.updateSessionDetails(session.id, { name, description });
      setIsEditing(false);
    } catch (error) {
      addNotification({
        type: 'error',
        message: t('details.updateFailed', { ns: 'sessions' })
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setName(session.name);
    setDescription(session.description || "");
    setIsEditing(false);
  };

  const handleViewResults = () => {
    const pdfEditorUrl = `/sessions/${session.id}/results`;
    window.open(pdfEditorUrl, '_blank');
  };

  const renderPdfSlide = () => (
    <div className="pdf-slide">
      <div className="pdf-container">
        {isLoadingPdf ? (
          <div className="pdf-loading">
            <div className="spinner"></div>
            <p>{t('details.loadingPdf')}</p>
          </div>
        ) : pdfError ? (
          <div className="pdf-error">
            <p>{pdfError}</p>
            <button onClick={() => window.location.reload()}>{t('details.retry')}</button>
          </div>
        ) : pdfUrl ? (
          <iframe
            src={pdfUrl}
            className="pdf-viewer"
            title={t('details.pdfViewerTitle')}
          />
        ) : (
          <div className="pdf-unavailable">
            <p>{t('details.pdfNotAvailable')}</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderDetailsSlide = () => (
    <div className="details-slide">
      <div className="session-details-content">
        <div className="session-info-section">
          <div className="form-group">
            <label className="form-label">{t('details.documentName')}</label>
            {isEditing ? (
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isUpdating}
              />
            ) : (
              <div className="form-display">{name}</div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">{t('details.description')}</label>
            {isEditing ? (
              <textarea
                className="form-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                disabled={isUpdating}
              />
            ) : (
              <div className="form-display">
                {description || t('details.noDescription')}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="slide-actions">
        {isEditing ? (
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancel}
              disabled={isUpdating}
            >
              {t('cancel', { ns: 'common' })}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSave}
              disabled={isUpdating || !name.trim()}
            >
              {isUpdating ? t('details.saving', { ns: 'sessions' }) : t('saveChanges', { ns: 'common' })}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setIsEditing(true)}
          >
            {t('edit', { ns: 'common' })}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content session-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <button className="close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          
          <h2 className="modal-title">{t('details.title')}</h2>
          
          {session.status === "completed" && (
            <button className="results-button" onClick={handleViewResults}>
              {t('details.viewResults')}
            </button>
          )}
        </div>

        <div className="carousel-container">
          <div className="carousel-slides" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
            {renderPdfSlide()}
            {renderDetailsSlide()}
          </div>
        </div>

        <div className="carousel-indicators">
          <button 
            className={`indicator ${currentSlide === 0 ? 'active' : ''}`}
            onClick={() => setCurrentSlide(0)}
          >
            {t('details.pdfTab')}
          </button>
          <button 
            className={`indicator ${currentSlide === 1 ? 'active' : ''}`}
            onClick={() => setCurrentSlide(1)}
          >
            {t('details.detailsTab')}
          </button>
        </div>
      </div>

      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default SessionDetails;
